import {
  HTTP_INTERCEPTORS,
  HttpClient,
  provideHttpClient,
  withInterceptors,
  withInterceptorsFromDi,
} from '@angular/common/http';
import { ApplicationConfig, importProvidersFrom, inject, provideAppInitializer } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { provideRouter, withComponentInputBinding, withRouterConfig } from '@angular/router';

import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';

import { environment } from '../environments/environment';
import { globalRoutes } from '../global.routes';
import { initializeLogging } from './initializers/logging.initializer';
import { mpsFetchBearer } from './initializers/mps-oauth2.initializer';
import { mpsFetchUser } from './initializers/mps-user.initializer';
import { mpsLoaderInterceptor } from './interceptors/mps-loader.interceptor';
import { mpsTokenInterceptor } from './interceptors/mps-token.interceptor';
import { MpsUnauthenticatedInterceptor } from './interceptors/mps-unauthenticated.interceptor';
import { LoggingService } from './services/logging.service';
import { MpsOauth2Service } from './services/mps-oauth2.service';
import { MpsUserService } from './services/mps-user.service';

// Local purpose
export function createTranslateLoader(http: HttpClient) {
  return new TranslateHttpLoader(http, './assets/i18n/', '.json');
}

export function httpTranslateLoader(http: HttpClient) {
  return new TranslateHttpLoader(http, environment.api.host + '/lokalise/', '.json');
}

export const mpsConfig: ApplicationConfig = {
  providers: [
    importProvidersFrom(
      // Angular packages
      BrowserModule,
      // Plugins packages
      TranslateModule.forRoot({
        defaultLanguage: environment.defaultLocale,
        loader: {
          provide: TranslateLoader,
          // TODO MPS: Change to "httpTranslateLoader" if you want to retrieve translation files for server
          useFactory: createTranslateLoader,
          deps: [HttpClient],
        },
      })
    ),
    provideHttpClient(withInterceptors([mpsTokenInterceptor]), withInterceptorsFromDi()),
    provideAnimationsAsync(),
    { provide: HTTP_INTERCEPTORS, useClass: mpsLoaderInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: MpsUnauthenticatedInterceptor, multi: true },
    { provide: 'OAUTH2_PROVIDERS_URL', useValue: environment.api.providers },
    { provide: 'API_BASE_URL', useValue: environment.api.host },
    { provide: 'LOADER_ON_HTTP', useValue: environment.loaderOnHttp },
    provideAppInitializer(() => {
      const initializerFn = mpsFetchBearer(inject(MpsOauth2Service), inject(MpsUserService));
      return initializerFn();
    }),
    provideAppInitializer(() => {
      const initializerFn = mpsFetchUser(inject(MpsUserService));
      return initializerFn();
    }),
    provideAppInitializer(() => {
      const initializerFn = initializeLogging(inject(LoggingService));
      return initializerFn();
    }),
    provideRouter(
      globalRoutes,
      // withDebugTracing(), // Uncomment the following line to enable routing debug trace
      withComponentInputBinding(),
      withRouterConfig({
        onSameUrlNavigation: 'reload',
      })
    ),
    provideAnimations(),
  ],
};
